/**
 * Shared shape for the product-listing page (PLP) reads.
 *
 * The route loader (server-render / entry priming) and the in-page
 * `useQuery` calls MUST build identical inputs, otherwise the loader's
 * prefetched payload can never be handed to the client query as
 * `initialData` and the shopper sees a full skeleton grid on arrival.
 * Keeping the builders here is the single source of truth for that.
 */

export const PAGE_SIZE = 24;

export type PlpSearch = {
  category?: string;
  brand?: string;
  condition?: string;
  part_type?: string;
  min_price?: number;
  max_price?: number;
  sort?: "newest" | "price_asc" | "price_desc" | "popular";
  q?: string;
  vehicle_type?: string;
  in_stock?: boolean;
  page?: number;
};

export function plpPage(search: PlpSearch | undefined): number {
  return Math.max(Number(search?.page ?? 1) || 1, 1);
}

export function buildProductListInput(search: PlpSearch | undefined, page: number) {
  const s = search ?? {};
  return {
    category: s.category ?? null,
    brand: s.brand ?? null,
    condition: s.condition ?? null,
    part_type: s.part_type ?? null,
    min_price: s.min_price ?? null,
    max_price: s.max_price ?? null,
    sort: s.sort ?? null,
    search: s.q ?? null,
    vehicle_type: s.vehicle_type ?? null,
    in_stock: s.in_stock ?? null,
    limit: page * PAGE_SIZE,
    offset: 0,
  };
}

export function buildFacetsInput(search: PlpSearch | undefined) {
  const s = search ?? {};
  return {
    category: s.category ?? null,
    brand: s.brand ?? null,
    condition: s.condition ?? null,
    part_type: s.part_type ?? null,
    vehicle_type: s.vehicle_type ?? null,
    search: s.q ?? null,
  };
}

/** Stable identity for "the loader payload matches what this render needs". */
export function plpCacheKey(input: unknown): string {
  return JSON.stringify(input);
}
